import { useItem } from 'dnd-timeline';
import { TriangleAlert } from 'lucide-react';
import { memo, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { BAR_PADDING } from '../scheduleDensity.js';
import type { ScheduleBar } from '../types.js';
import { isBookingBar, shortRef } from './ScheduleBarCard.js';
import { barStyleFor } from './barStyle.js';
import type { DayCell } from './dayCells.js';
import { type ScheduleView, mytClock, mytDayLabel } from './dropLogic.js';
import { usePopoverShell } from './usePopoverShell.js';

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

/** FR-002: what the cell says. Week: count, hours, blocks, unpaid. Month: the count only. */
export function cellText(cell: DayCell, view: ScheduleView): string {
  if (view === 'month') return String(cell.rentals + cell.blocks);
  const parts: string[] = [];
  if (cell.rentals > 0) parts.push(plural(cell.rentals, 'rental'), `${cell.hours} h`);
  if (cell.blocks > 0)
    parts.push(
      cell.rentals > 0 ? `+${plural(cell.blocks, 'block')}` : plural(cell.blocks, 'block'),
    );
  if (cell.unpaid) parts.push('unpaid');
  return parts.join(' · ');
}

/** The summary the list's footer and the accessible name use, whatever the view. */
function summary(cell: DayCell): string {
  const parts: string[] = [];
  if (cell.rentals > 0) parts.push(plural(cell.rentals, 'rental'), `${cell.hours} h`);
  if (cell.blocks > 0) parts.push(plural(cell.blocks, 'block'));
  return parts.join(' · ');
}

/** R3: the day's booked share deepens the fill, from a light tint to half the brand blue. */
const fillPercent = (share: number) => Math.round(12 + Math.min(share, 1) * 38);

function lineName(bar: ScheduleBar): string {
  if (bar.customer_name) return bar.customer_name;
  if (isBookingBar(bar) && bar.booking_id) return `Booking ${shortRef(bar.booking_id)}`;
  return bar.reason ?? '';
}

export interface DayCellItemProps {
  cell: DayCell;
  carLabel: string;
  view: ScheduleView;
  onOpenBar?: (bar: ScheduleBar) => void;
  onOpenDay?: (date: string) => void;
}

/**
 * 368 (#1236, design A) — one car-day's short rentals and blocks as one cell: a disabled dnd-timeline item across the
 * whole Malaysia day (never dragged or dropped on, FR-007). It opens a list of its items; each line opens like its bar,
 * and "Open day" goes to the Day view, where items are dragged as before.
 */
export const DayCellItem = memo(function DayCellItem({
  cell,
  carLabel,
  view,
  onOpenBar,
  onOpenDay,
}: DayCellItemProps) {
  const { setNodeRef, itemStyle, itemContentStyle } = useItem({
    id: cell.id,
    span: { start: cell.start, end: cell.end },
    disabled: true,
    data: { cell },
  });
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const close = useCallback(() => setAnchor(null), []);
  const day = mytDayLabel(cell.start);
  const strong = cell.share > 0.6;
  const name = [
    carLabel,
    day,
    summary(cell),
    cell.unpaid ? 'Unpaid' : null,
    cell.attention ? 'Needs attention' : null,
  ]
    .filter(Boolean)
    .concat('Shows the day’s rentals')
    .join(' · ');
  const open = (el: HTMLElement) => setAnchor(el.getBoundingClientRect());

  return (
    <div ref={setNodeRef} style={itemStyle} className={BAR_PADDING}>
      <div style={itemContentStyle}>
        {/* A 2 px gap so neighbouring days' cells never touch (the slot's own padding is set inline by dnd-timeline). */}
        <div className="h-full w-full px-0.5">
          {/* biome-ignore lint/a11y/useSemanticElements: a div keeps the bar's layout; it carries the button role and keys */}
          <div
            role="button"
            tabIndex={0}
            data-testid={`schedule-cell-${cell.carId}-${cell.date}`}
            data-unpaid={String(cell.unpaid)}
            data-attention={String(cell.attention)}
            data-share={cell.share.toFixed(3)}
            aria-label={name}
            aria-haspopup="dialog"
            aria-expanded={anchor !== null}
            onClick={(e) => open(e.currentTarget)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                open(e.currentTarget);
              }
            }}
            style={{
              // srgb, not oklch: an oklch mix interpolates hue, and the canvas's nominal hue turns the blue olive.
              background: `color-mix(in srgb, var(--color-brand-blue) ${fillPercent(cell.share)}%, var(--color-canvas))`,
            }}
            className={[
              '@container flex h-full w-full min-w-0 cursor-pointer items-center justify-center gap-1 overflow-hidden rounded border border-brand-blue px-1 text-xs font-semibold whitespace-nowrap focus-visible:outline-2 focus-visible:outline-brand-blue',
              cell.unpaid ? 'border-dashed' : '',
              strong ? 'text-white' : 'text-brand-blue-dark',
            ].join(' ')}
          >
            {cell.attention && (
              <TriangleAlert
                aria-hidden="true"
                data-testid={`schedule-cell-attention-${cell.carId}-${cell.date}`}
                className="h-3 w-3 shrink-0"
              />
            )}
            <span className="min-w-0 truncate">{cellText(cell, view)}</span>
          </div>
        </div>
      </div>
      {anchor &&
        createPortal(
          <DayCellList
            cell={cell}
            carLabel={carLabel}
            anchorRect={anchor}
            onDismiss={close}
            onOpenBar={(b) => {
              close();
              onOpenBar?.(b);
            }}
            onOpenDay={(d) => {
              close();
              onOpenDay?.(d);
            }}
          />,
          document.body,
        )}
    </div>
  );
});

/** FR-003: the car-day's items in time order; each line opens like its bar. */
function DayCellList({
  cell,
  carLabel,
  anchorRect,
  onDismiss,
  onOpenBar,
  onOpenDay,
}: {
  cell: DayCell;
  carLabel: string;
  anchorRect: DOMRect;
  onDismiss: () => void;
  onOpenBar: (bar: ScheduleBar) => void;
  onOpenDay: (date: string) => void;
}) {
  const { boxRef, style } = usePopoverShell(anchorRect, onDismiss);
  return (
    <div
      ref={boxRef}
      style={style}
      // biome-ignore lint/a11y/useSemanticElements: #950 role="dialog" on a positioned panel is the intended ARIA pattern; native <dialog> is hidden until opened and brings its own positioning
      role="dialog"
      aria-label={`${carLabel} · ${mytDayLabel(cell.start)}`}
      data-testid="schedule-cell-list"
      className="w-72 rounded-lg border border-hairline-strong bg-canvas p-3 text-sm shadow-lg"
    >
      <h3 className="mb-2 text-sm font-semibold text-ink">{`${carLabel} · ${mytDayLabel(cell.start)}`}</h3>
      <ul className="flex flex-col gap-1.5">
        {cell.items.map((b) => {
          const style = barStyleFor(b);
          return (
            <li key={b.id}>
              <button
                type="button"
                onClick={() => onOpenBar(b)}
                className="grid w-full grid-cols-[5.5rem_1fr_auto] items-center gap-2 rounded-md border border-hairline px-2 py-1.5 text-left hover:bg-surface"
              >
                <span className="tabular-nums text-ink">{`${mytClock(Date.parse(b.start_at))}–${mytClock(Date.parse(b.end_at))}`}</span>
                <span className="min-w-0 truncate text-slate">{lineName(b)}</span>
                <span className="rounded-sm px-1 text-[10px] font-semibold uppercase ring-1 ring-current text-brand-blue">
                  {style.word}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <div className="mt-2 flex items-center justify-between text-xs">
        <span className="text-steel">{summary(cell)}</span>
        <button
          type="button"
          onClick={() => onOpenDay(cell.date)}
          className="font-medium text-brand-blue hover:underline"
        >
          Open day
        </button>
      </div>
    </div>
  );
}
